import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import NumberPad from "./NumberPad";
import CameraVideo, { CameraVideoRef } from "./CameraVideo";
import Directory from "./Directory";
import MessageModal, { MessageType } from "./MessageModal";
import { useTranslation, SupportedLanguage } from "../i18n";
import {
  validateQR,
  getDeviceInfo,
  verifyPin,
  verifyRoomDeliveryPin,
  callManager,
  callTenant,
  endSession,
  getModules,
} from "../services/api";
import {
  ValidateQRResponse,
  DeviceInfoResponse,
  Room,
  CallManagerResponse,
  CallTenantResponse,
  Delivery,
  Module,
  GetModulesResponse,
} from "../services/types";

interface HomePageProps {
  deviceId?: string;
  qrSecret?: string;
}

const HomePage: React.FC<HomePageProps> = ({ deviceId, qrSecret }) => {
  const navigate = useNavigate();
  const { t, translate, language, setLanguage } = useTranslation();
  const [showNumberPad, setShowNumberPad] = useState(false);
  const [numberPadType, setNumberPadType] = useState<"delivery" | "room">(
    "room"
  );
  const [enteredCode, setEnteredCode] = useState("");
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfoResponse | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrValidated, setQrValidated] = useState(false);
  const [showDirectory, setShowDirectory] = useState(false);
  const [isCallingManager, setIsCallingManager] = useState(false);
  const [isCallingRoom, setIsCallingRoom] = useState(false);
  const [callingRoomId, setCallingRoomId] = useState<string | null>(null); // Add this state

  // 消息弹窗状态
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageType, setMessageType] = useState<MessageType>("success");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageText, setMessageText] = useState("");

  // 使用 useRef 来跟踪是否已经尝试过验证
  const hasAttemptedValidation = useRef(false);

  // Client-side session expiration timer
  const sessionExpirationTimer = useRef<NodeJS.Timeout | null>(null);

  // Camera ref for photo capture
  const cameraRef = useRef<CameraVideoRef>(null);

  // State for captured photo
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(null);

  const handleNumberPadOpen = (type: "delivery" | "room") => {
    setNumberPadType(type);
    setShowNumberPad(true);
    setEnteredCode("");
  };

  const handleNumberPadClose = () => {
    setShowNumberPad(false);
    setEnteredCode("");
  };

  // 检查现有 token 是否有效
  useEffect(() => {
    const checkExistingToken = async () => {
      // First, check if session is already expired
      if (checkAndHandleExpiredSession()) {
        return; // Session was expired and handled, don't proceed
      }

      // 从 localStorage 获取已保存的 token 和设备信息
      const savedToken = localStorage.getItem("sessionToken");
      const savedDeviceId = localStorage.getItem("deviceId");
      const savedExpiresAt = localStorage.getItem("tokenExpiresAt");

      // 检查 token 是否存在且未过期
      if (savedToken && savedDeviceId === deviceId && savedExpiresAt) {
        const expiresAt = parseInt(savedExpiresAt);
        const now = Date.now();

        if (now < expiresAt) {
          try {
            // 验证 token 是否仍然有效
            const deviceData = await getDeviceInfo(savedToken);
            if (deviceData) {
              // Token 仍然有效
              setSessionToken(savedToken);
              setDeviceInfo(deviceData);
              setQrValidated(true);

              // Restart the expiration timer for remaining time
              const remainingTime = expiresAt - now;
              if (remainingTime > 0) {
                sessionExpirationTimer.current = setTimeout(() => {
                  // Clear session data
                  setSessionToken(null);
                  setQrValidated(false);
                  localStorage.removeItem("sessionToken");
                  localStorage.removeItem("deviceId");
                  localStorage.removeItem("tokenExpiresAt");

                  // End session on server
                  endSession(savedToken).catch((error) =>
                    console.error("Error ending expired session:", error)
                  );

                  showMessage(
                    "error",
                    t.messages.sessionExpired,
                    t.messages.sessionExpiredMessage
                  );
                }, remainingTime);
              }

              // 获取模块信息
              const modulesData = await getModules(savedToken);
              if (modulesData.success) {
                setModules(modulesData.modules);
              }
              return;
            }
          } catch (error) {
            console.log("Saved token is invalid, will re-validate QR code");
          }
        }
      }

      // 清除无效的 token
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("deviceId");
      localStorage.removeItem("tokenExpiresAt");

      // 如果没有有效 token，进行 QR 验证
      validateQRCode();
    };

    checkExistingToken();
  }, [deviceId]); // 只依赖 deviceId

  // Periodic expiration check - runs every minute
  useEffect(() => {
    // Set up periodic expiration check every minute
    const expirationCheckInterval = setInterval(() => {
      checkAndHandleExpiredSession();
    }, 60000); // Check every 60 seconds

    // Make function available globally for manual testing
    (window as any).checkExpiration = checkAndHandleExpiredSession;

    // Cleanup interval on unmount
    return () => {
      clearInterval(expirationCheckInterval);
      delete (window as any).checkExpiration;
    };
  }, [sessionToken]); // Run when sessionToken changes

  // QR码验证和获取session token
  const validateQRCode = async () => {
    if (deviceId && qrSecret && !hasAttemptedValidation.current) {
      hasAttemptedValidation.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const result: ValidateQRResponse = await validateQR(deviceId, qrSecret);

        if (
          result.success &&
          (result.sessionToken || result.session?.sessionToken)
        ) {
          const token = result.sessionToken || result.session?.sessionToken;
          if (token) {
            setSessionToken(token);
            setQrValidated(true);

            // 保存 token 到 localStorage
            localStorage.setItem("sessionToken", token);
            localStorage.setItem("deviceId", deviceId);

            // Set client-side 5-minute expiration (override server setting)
            const clientExpiresAt = Date.now() + 5 * 60 * 1000; // 5 minutes from now
            localStorage.setItem("tokenExpiresAt", clientExpiresAt.toString());

            // Start the 5-minute expiration timer
            startSessionExpirationTimer();

            // 获取设备信息
            const deviceData = await getDeviceInfo(token);
            if (deviceData) {
              setDeviceInfo(deviceData);
            }

            // 获取模块信息
            const modulesData = await getModules(token);
            if (modulesData.success) {
              setModules(modulesData.modules);
            }
          }
        } else {
          setError(result.error || "QR code validation failed");
        }
      } catch (err) {
        setError("QR code validation error");
        console.error("QR validation error:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 检查模块是否启用的辅助函数
  const isModuleEnabled = (moduleType: string): boolean => {
    return modules.some(
      (module) => module.type === moduleType && module.isEnabled
    );
  };

  // 获取模块显示名称的辅助函数
  const getModuleDisplayName = (moduleType: string): string => {
    const module = modules.find((m) => m.type === moduleType);
    return module?.displayName || moduleType;
  };

  // 按显示顺序排序模块
  const getSortedEnabledModules = (): Module[] => {
    return modules
      .filter((module) => module.isEnabled)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  };

  // Client-side session expiration functions
  const startSessionExpirationTimer = () => {
    // Clear any existing timer
    if (sessionExpirationTimer.current) {
      clearTimeout(sessionExpirationTimer.current);
    }

    // Capture the current token value to avoid React closure issues
    const currentToken = sessionToken;

    // Set 5-minute timer (300,000 milliseconds)
    sessionExpirationTimer.current = setTimeout(() => {
      // Use captured token, not state variable (avoids closure issues)
      if (currentToken) {
        // Clear session data
        setSessionToken(null);
        setQrValidated(false);
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("deviceId");
        localStorage.removeItem("tokenExpiresAt");

        // End session on server using captured token
        endSession(currentToken).catch((error) =>
          console.error("Error ending expired session:", error)
        );

        // Show expiration message
        showMessage(
          "error",
          t.messages.sessionExpired,
          t.messages.sessionExpiredMessage
        );
      }
    }, 5 * 60 * 1000); // 5 minutes = 300,000ms
  };

  const clearSessionExpirationTimer = () => {
    if (sessionExpirationTimer.current) {
      clearTimeout(sessionExpirationTimer.current);
      sessionExpirationTimer.current = null;
    }
  };

  // Check and handle already-expired sessions
  const checkAndHandleExpiredSession = () => {
    const savedExpiresAt = localStorage.getItem("tokenExpiresAt");
    const savedToken = localStorage.getItem("sessionToken");

    if (savedExpiresAt && savedToken) {
      const expiresAt = parseInt(savedExpiresAt);
      const now = Date.now();

      if (now >= expiresAt) {
        // Session already expired - clear it immediately
        setSessionToken(null);
        setQrValidated(false);
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("deviceId");
        localStorage.removeItem("tokenExpiresAt");

        // End session on server
        endSession(savedToken).catch((error) =>
          console.error("Error ending expired session:", error)
        );

        // Show expiration message
        showMessage(
          "error",
          t.messages.sessionExpired,
          t.messages.sessionExpiredShort
        );

        return true; // Session was expired and handled
      }
    }
    return false; // Session not expired
  };

  // 添加页面卸载时的清理逻辑
  useEffect(() => {
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      if (sessionToken) {
        try {
          // 使用 sendBeacon 确保请求能够发送
          const data = JSON.stringify({ sessionToken });
          navigator.sendBeacon(
            `${
              process.env.REACT_APP_API_URL ||
              "https://api.entrioai.com/entrio/api"
            }/virtual-intercom/user/end-session`,
            data
          );
        } catch (error) {
          console.error("Error ending session on page unload:", error);
        }
      }
    };

    // 监听页面卸载事件
    window.addEventListener("beforeunload", handleBeforeUnload);

    // 清理函数
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearSessionExpirationTimer(); // Clear the session expiration timer
    };
  }, [sessionToken]);

  const handleCodeSubmit = async (code: string, selectedOption?: string) => {
    if (!sessionToken) {
      showMessage("error", t.messages.error, t.messages.pleaseVerifyQR);
      return;
    }

    if (!selectedOption) {
      showMessage(
        "error",
        t.messages.error,
        numberPadType === "delivery"
          ? t.messages.pleaseSelectDelivery
          : t.messages.pleaseSelectRoom
      );
      return;
    }

    setIsLoading(true);
    try {
      // Capture photo before PIN verification only if enabled
      const eventPhoto = deviceInfo?.device?.eventPhotoCaptureEnabled
        ? await captureEventPhoto()
        : null;

      // 使用新的 API
      const result = await verifyRoomDeliveryPin(
        sessionToken,
        numberPadType,
        numberPadType === "room" ? selectedOption : undefined,
        numberPadType === "delivery" ? selectedOption : undefined,
        code,
        eventPhoto || undefined
      );

      if (result.success && result.result.success) {
        const doorOpen = result.result.doorOpen;

        if (doorOpen?.success) {
          // Door opened successfully
          const successMessage =
            numberPadType === "delivery"
              ? translate(t.messages.deliveryPinVerified, {
                  name: result.result.delivery?.name || selectedOption,
                })
              : translate(t.messages.roomPinVerified, {
                  name: result.result.room?.name || selectedOption,
                });

          showMessage("success", t.messages.success, successMessage);
        } else {
          // PIN valid but door failed to open
          showMessage(
            "error",
            t.messages.doorFailedToOpen,
            t.messages.doorFailedMessage
          );
        }
      } else {
        // Check if this is a session expiration error
        const errorMessage =
          result.error || result.message || "PIN verification failed";

        if (
          result.statusCode === 401 ||
          errorMessage.includes("Session not found") ||
          errorMessage.includes("session expired") ||
          errorMessage.includes("Unauthorized") ||
          errorMessage.includes("Please refresh the page")
        ) {
          // Clear expired session
          setSessionToken(null);
          setQrValidated(false);
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("tokenExpiresAt");

          showMessage(
            "error",
            t.messages.sessionExpired,
            t.messages.sessionExpiredContinue
          );
        } else {
          const displayMessage =
            result.result?.success === false
              ? t.messages.invalidPin
              : errorMessage;
          showMessage(
            "error",
            t.messages.pinVerificationFailed,
            `${t.messages.pinVerificationFailed}: ${displayMessage}`
          );
        }
      }
    } catch (err) {
      showMessage("error", t.messages.error, t.messages.pinVerificationError);
      console.error("PIN verification error:", err);
    } finally {
      setIsLoading(false);
      handleNumberPadClose();
    }
  };

  // 显示消息弹窗的通用函数
  const showMessage = (
    type: MessageType,
    title: string,
    message: string,
    autoCloseDelay: number = 3000
  ) => {
    setMessageType(type);
    setMessageTitle(title);
    setMessageText(message);
    setShowMessageModal(true);
  };

  // Photo capture handler
  const handlePhotoCapture = (photoBlob: Blob) => {
    setCapturedPhoto(photoBlob);
  };

  // Helper function to capture photo from camera
  const captureEventPhoto = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!cameraRef.current) {
        resolve(null);
        return;
      }

      let photoReceived = false;

      // Store the original callback
      const videoElement = document.querySelector(
        ".camera-video"
      ) as HTMLVideoElement;

      if (
        !videoElement ||
        !videoElement.videoWidth ||
        !videoElement.videoHeight
      ) {
        resolve(null);
        return;
      }

      try {
        // Directly capture from video element
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) {
          resolve(null);
          return;
        }

        // Set canvas dimensions to match video
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        // Draw current video frame to canvas
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob && !photoReceived) {
              photoReceived = true;
              resolve(blob);
            } else if (!photoReceived) {
              photoReceived = true;
              resolve(null);
            }
          },
          "image/jpeg",
          0.8 // Quality setting
        );

        // Timeout fallback after 2 seconds
        setTimeout(() => {
          if (!photoReceived) {
            photoReceived = true;
            resolve(null);
          }
        }, 2000);
      } catch (error) {
        console.error("Error capturing photo:", error);
        if (!photoReceived) {
          photoReceived = true;
          resolve(null);
        }
      }
    });
  };

  const handleMessageModalClose = () => {
    setShowMessageModal(false);
    setMessageTitle("");
    setMessageText("");
  };

  const handleDirectoryOpen = () => {
    if (!sessionToken) {
      showMessage("error", t.messages.error, t.messages.pleaseVerifyQR);
      return;
    }
    setShowDirectory(true);
  };

  const handleDirectoryClose = () => {
    setShowDirectory(false);
  };

  const handleCallRoom = async (room: Room, userIds?: string[]) => {
    if (!sessionToken) {
      showMessage("error", t.messages.error, t.messages.pleaseVerifyQR);
      return;
    }

    setIsCallingRoom(true);
    setCallingRoomId(room.id); // Set the specific room ID being called
    setError(null);

    try {
      // Capture photo before making the call only if enabled
      const eventPhoto = deviceInfo?.device?.eventPhotoCaptureEnabled
        ? await captureEventPhoto()
        : null;

      const result: CallTenantResponse = await callTenant(
        sessionToken,
        room.id,
        eventPhoto || undefined,
        userIds
      );

      if (result.success && result.data) {
        // 导航到视频通话页面，传递连接信息
        navigate("/video-call", {
          state: {
            callData: result.data,
            deviceInfo: deviceInfo,
            sessionToken: sessionToken,
            callType: "resident" as const,
            callTarget: {
              name: room.name,
              room: room.name,
            },
          },
        });
      } else {
        // Check if this is a session expiration error
        const errorMessage = result.error || "Failed to call room";

        if (
          result.statusCode === 401 ||
          errorMessage.includes("Session not found") ||
          errorMessage.includes("session expired") ||
          errorMessage.includes("Unauthorized") ||
          errorMessage.includes("Please refresh the page")
        ) {
          // Clear expired session
          setSessionToken(null);
          setQrValidated(false);
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("tokenExpiresAt");

          setError(errorMessage);
          showMessage(
            "error",
            t.messages.sessionExpired,
            t.messages.sessionExpiredContinue
          );
        } else {
          setError(errorMessage);
          showMessage(
            "error",
            t.messages.callRoomFailed,
            `${t.messages.callRoomFailed}: ${errorMessage}`
          );
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      showMessage(
        "error",
        t.messages.callRoomError,
        `${t.messages.callRoomError}: ${errorMessage}`
      );
      console.error("Call room error:", err);
    } finally {
      setIsCallingRoom(false);
      setCallingRoomId(null); // Clear the calling room ID
      setShowDirectory(false);
    }
  };

  const handleCallManager = async () => {
    if (!sessionToken) {
      showMessage("error", t.messages.error, t.messages.pleaseVerifyQR);
      return;
    }

    setIsCallingManager(true);
    setError(null);

    try {
      // Capture photo before making the call only if enabled
      const eventPhoto = deviceInfo?.device?.eventPhotoCaptureEnabled
        ? await captureEventPhoto()
        : null;

      const result: CallManagerResponse = await callManager(
        sessionToken,
        eventPhoto || undefined
      );

      if (result.success && result.data) {
        // 导航到视频通话页面，传递连接信息
        navigate("/video-call", {
          state: {
            callData: result.data,
            deviceInfo: deviceInfo,
            sessionToken: sessionToken,
            callType: "manager" as const,
          },
        });
      } else {
        // Check if this is a session expiration error
        const errorMessage = result.error || "Failed to call manager";

        if (
          result.statusCode === 401 ||
          errorMessage.includes("Session not found") ||
          errorMessage.includes("session expired") ||
          errorMessage.includes("Unauthorized") ||
          errorMessage.includes("Please refresh the page")
        ) {
          // Clear expired session
          setSessionToken(null);
          setQrValidated(false);
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("tokenExpiresAt");

          setError(errorMessage);
          showMessage(
            "error",
            t.messages.sessionExpired,
            t.messages.sessionExpiredContinue
          );
        } else {
          setError(errorMessage);
          showMessage(
            "error",
            t.messages.callManagerFailed,
            `${t.messages.callManagerFailed}: ${errorMessage}`
          );
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      showMessage(
        "error",
        t.messages.callManagerError,
        `${t.messages.callManagerError}: ${errorMessage}`
      );
      console.error("Call manager error:", err);
    } finally {
      setIsCallingManager(false);
    }
  };

  return (
    <div className="home-container">
      {/* 头部区域 */}
      <div className="header-section">
        <div className="header-top">
          {deviceInfo?.device.logoUrl && (
            <div className="logo-box">
              <img
                src={deviceInfo.device.logoUrl}
                alt="Logo"
                className="device-logo"
              />
            </div>
          )}
          <div className="header-info">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <h1 className="logo-text">{t.home.welcome}</h1>
              {/* Language Dropdown */}
              <select
                value={language}
                onChange={(e) =>
                  setLanguage(e.target.value as SupportedLanguage)
                }
                className="language-select-dropdown"
              >
                <option value="en">EN - English</option>
                <option value="fr">FR - Français</option>
                <option value="es">ES - Español</option>
              </select>
            </div>
            {deviceInfo && (
              <>
                <p className="apartment-name">
                  {deviceInfo.device.apartment.name}
                </p>
                <p className="apartment-address">
                  {deviceInfo.device.apartment.street},{" "}
                  {deviceInfo.device.apartment.city},{" "}
                  {deviceInfo.device.apartment.state}{" "}
                  {deviceInfo.device.apartment.zipCode}
                </p>
              </>
            )}
            {isLoading && (
              <span className="status-badge loading-badge">
                {t.home.verifyingQR}
              </span>
            )}
            {error && (
              <span className="status-badge error-badge">
                {t.home.error}: {error}
              </span>
            )}
            {qrValidated && sessionToken && (
              <span className="status-badge success-badge">
                {t.home.verified}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="main-content-area">
        {/* 视频区域 */}
        <div className="video-section">
          <CameraVideo
            ref={cameraRef}
            className="video-camera"
            onError={(error) => console.log("Camera error:", error)}
            onSuccess={() => console.log("Camera started successfully")}
            onPhotoCapture={handlePhotoCapture}
          />
        </div>

        {/* 主要操作按钮 */}
        {/* <button className="main-action-button">
          Directory
        </button> */}

        {/* 功能按钮区域 - 动态布局 */}
        <div
          className={`function-buttons function-buttons-${
            getSortedEnabledModules().length
          }`}
        >
          {isModuleEnabled("DIRECTORY") && (
            <button
              className={`function-btn directory-btn ${
                !sessionToken ? "disabled" : ""
              }`}
              onClick={handleDirectoryOpen}
              disabled={!sessionToken || isLoading}
            >
              <div className="btn-icon">
                <img src="/images/directory.svg" alt={t.buttons.directory} />
              </div>
              <span className="btn-text">{t.buttons.directory}</span>
            </button>
          )}

          {isModuleEnabled("DELIVERY") && (
            <button
              className={`function-btn delivery-btn ${
                !sessionToken ? "disabled" : ""
              }`}
              onClick={() => handleNumberPadOpen("delivery")}
              disabled={!sessionToken || isLoading}
            >
              <div className="btn-icon">
                <img src="/images/delivery.svg" alt={t.buttons.delivery} />
              </div>
              <span className="btn-text">{t.buttons.delivery}</span>
            </button>
          )}

          {isModuleEnabled("VISITOR_CHECKIN") && (
            <button
              className={`function-btn visitor-checkin-btn ${
                !sessionToken ? "disabled" : ""
              }`}
              onClick={() => {
                navigate("/visitor-checkin", {
                  state: {
                    deviceId: deviceId,
                    sessionToken: sessionToken,
                  },
                });
              }}
              disabled={!sessionToken || isLoading}
            >
              <div className="btn-icon">
                <img
                  src="/images/visitor-checkin.svg"
                  alt={t.buttons.visitorCheckIn}
                />
              </div>
              <span className="btn-text">{t.buttons.visitorCheckIn}</span>
            </button>
          )}

          {isModuleEnabled("DOOR_PIN") && (
            <button
              className={`function-btn door-btn ${
                !sessionToken ? "disabled" : ""
              }`}
              onClick={() => handleNumberPadOpen("room")}
              disabled={!sessionToken || isLoading}
            >
              <div className="btn-icon">
                <img src="/images/icon-door-pin.svg" alt={t.buttons.doorPin} />
              </div>
              <span className="btn-text">{t.buttons.doorPin}</span>
            </button>
          )}

          {isModuleEnabled("GUEST_PASS") && (
            <button
              className={`function-btn guest-pass-btn ${
                !sessionToken ? "disabled" : ""
              }`}
              onClick={() => {
                navigate("/guest-pass", {
                  state: {
                    deviceId: deviceId,
                    sessionToken: sessionToken,
                    deviceInfo: deviceInfo,
                  },
                });
              }}
              disabled={!sessionToken || isLoading}
            >
              <div className="btn-icon">
                <img
                  src="/images/icon-guest-pass.svg"
                  alt={t.buttons.guestPass}
                />
              </div>
              <span className="btn-text">{t.buttons.guestPass}</span>
            </button>
          )}

          {isModuleEnabled("CALL_RECEPTION") && (
            <button
              className={`function-btn call-reception-btn ${
                !sessionToken ? "disabled" : ""
              }`}
              onClick={handleCallManager}
              disabled={!sessionToken || isLoading || isCallingManager}
            >
              <div className="btn-icon">
                <img src="/images/office.svg" alt={t.buttons.callReception} />
              </div>
              <span className="btn-text">
                {isCallingManager
                  ? t.directory.calling
                  : t.buttons.callReception}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* 数字键盘弹窗 */}
      {showNumberPad && (
        <NumberPad
          type={numberPadType}
          onClose={handleNumberPadClose}
          onSubmit={handleCodeSubmit}
          title={
            numberPadType === "delivery"
              ? t.numberPad.deliveryTitle
              : t.numberPad.roomTitle
          }
          deliveries={deviceInfo?.device?.deliveries}
          rooms={deviceInfo?.device?.building?.rooms}
        />
      )}

      {/* Directory 弹窗 */}
      {showDirectory && deviceInfo && (
        <Directory
          rooms={deviceInfo.device.building?.rooms ?? []}
          onClose={handleDirectoryClose}
          onCallRoom={handleCallRoom}
          callingRoomId={callingRoomId}
          roomSortBy={deviceInfo.device.roomSortBy}
          roomDisplayFormat={deviceInfo.device.roomDisplayFormat}
        />
      )}

      {/* 通用消息弹窗 */}
      <MessageModal
        isOpen={showMessageModal}
        type={messageType}
        title={messageTitle}
        message={messageText}
        onClose={handleMessageModalClose}
        autoCloseDelay={3000}
      />
    </div>
  );
};

export default HomePage;
